//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  AverageStoryEngagement,
  AverageStoryEngagementRequest,
  BasicUserInfo,
  BrandAffinityMediaRequest,
  BrandAffinityMediaResponse,
  BrandAffinityRequest,
  BrandAffinityResponse,
  ContentDistributionInsightRequest,
  ContentDistributionInsightResponse,
  CreateAudienceEstimateRequest,
  CreateAudienceEstimateResponse,
  CreatorAffableMediaRequest,
  CreatorAffableMediaResponse,
  CreatorCostEstimates,
  CreatorCostEstimatesRequest,
  CreatorDetailsExportRequest,
  CreatorDetailsUser,
  CreatorDetailsUserRequest,
  CreatorMediaRequest,
  CreatorMediaResponse,
  CreatorMediaSafetyStatsRequest,
  CreatorMediaSafetyStatsResponse,
  CreatorSearchRequest,
  CreatorSocialPlatforms,
  CreatorSocialPlatformsRequest,
  CrossPlatformCreatorsLookupRequest,
  CrossPlatformCreatorsLookupResponse,
  DetailedUserInfo,
  EngagementTimelineRequest,
  EngagementTimelineResponse,
  FollowerTimelineRequest,
  FollowerTimelineResponse,
  SearchCreatorUsersResponse,
  SimilarCreatorsRequest,
  SimilarCreatorsResponse,
} from '@apis/creator-details/creatorDetailsApiModel';
import {
  InfluencerMetadata,
  UpdateInfluencerMetadataApiRequest,
  UpdateInfluencerMetadataResponse,
} from '@apis/creator-details/creatorMetadataApiModel';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { Serialized } from '@utils/types/Serialized';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const CREATOR_DETAILS_USERS_DEFAULT = '/api/v1/users';
const CREATOR_DETAILS_INSIGHTS_DEFAULT = '/api/v1/insights';

function influencersGatewayV2Fetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'influencers_gateway',
    `/api/v2${endpoint}`,
    init,
  );
}

const creatorDetailsUsersFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'affable_gateway',
    `${CREATOR_DETAILS_USERS_DEFAULT}${endpoint}`,
    init,
  );
};

const creatorDetailsInsightsFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'affable_gateway',
    `${CREATOR_DETAILS_INSIGHTS_DEFAULT}${endpoint}`,
    init,
  );
};

/**
 * Deserializes the response for creator media safety stats, converting date strings to Date objects.
 */
function deserializeCreatorMediaSafetyStatsResponse(
  response: Serialized<{ data: CreatorMediaSafetyStatsResponse }>,
): { data: CreatorMediaSafetyStatsResponse } {
  return {
    data: {
      ...response.data,
      lastProcessedMediaAt: response.data.lastProcessedMediaAt
        ? new Date(response.data.lastProcessedMediaAt)
        : undefined,
    },
  };
}

/**
 * Search for influencers matching search term
 *
 * GET /api/v1/users/lookup -  For Cross Platform Search Enabled
 */
export const searchCreatorsCrossPlatform = async (
  req: CrossPlatformCreatorsLookupRequest,
  init?: RequestInit,
): Promise<CrossPlatformCreatorsLookupResponse> => {
  const { limit, networks, offset, term } = req;
  const params = makeParams({ term, networks, limit, offset });
  const res = await creatorDetailsUsersFetch(`/lookup?${params}`, {
    ...init,
    method: 'GET',
  });

  const filterResults = await res.json<{
    data: CrossPlatformCreatorsLookupResponse;
  }>();

  return filterResults.data;
};

// TODO : PD-300016 - Remove this after the new API is rolled out
/**
 * Get a creator by username includes bio, audience, and engagement data
 *
 * GET /api/v1/users/:network/:username
 */
export const getCreatorDetailsUser = async (
  req: CreatorDetailsUserRequest,
  init?: RequestInit,
): Promise<CreatorDetailsUser> => {
  const { network, username } = req;

  const res = await creatorDetailsUsersFetch(`/${network}/${username}`, {
    ...init,
    method: 'GET',
  });

  const userResults = await res.json<{ data: CreatorDetailsUser }>((res) => ({
    data: {
      ...res.data,
      trackedDate: res.data.trackedDate
        ? new Date(res.data.trackedDate)
        : undefined,
    },
  }));

  return userResults.data;
};

/**
 * Get basic information for a creator
 *
 * GET /api/v1/users/basic-info/:network/:username
 */
export const getCreatorBasicInfo = async (
  req: CreatorDetailsUserRequest,
  init?: RequestInit,
): Promise<BasicUserInfo> => {
  const { network, username } = req;
  const res = await creatorDetailsUsersFetch(
    `/basic-info/${network}/${username}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const response = await res.json<{ data: BasicUserInfo }>((res) => ({
    data: {
      ...res.data,
      trackedDate: res.data.trackedDate
        ? new Date(res.data.trackedDate)
        : undefined,
    },
  }));

  return response.data;
};

/**
 * Get detailed information for a creator
 *
 * GET /api/v1/users/detailed-info/:network/:username
 */
export const getCreatorDetailedInfo = async (
  req: CreatorDetailsUserRequest,
  init?: RequestInit,
): Promise<DetailedUserInfo> => {
  const { network, username } = req;

  const res = await creatorDetailsUsersFetch(
    `/detailed-info/${network}/${username}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const response = await res.json<{ data: DetailedUserInfo }>();
  return response.data;
};

/**
 * Get a creator social platforms
 * (details about a users other social profiles)
 *
 * GET /api/v1/users/:network/:username/social-platforms
 */
export const getCreatorSocialPlatforms = async (
  req: CreatorSocialPlatformsRequest,
  init?: RequestInit,
): Promise<CreatorSocialPlatforms> => {
  const { network, username } = req;

  const res = await creatorDetailsUsersFetch(
    `/${network}/${username}/social-platforms`,
    {
      ...init,
      method: 'GET',
    },
  );

  const socialPlatformsResults = await res.json<{
    data: CreatorSocialPlatforms;
  }>();

  return socialPlatformsResults.data;
};

/**
 * Get a creator cost estimates
 *
 * GET /api/v1/users/:network/:username/cost-estimates
 */
export const getCreatorCostEstimates = async (
  req: CreatorCostEstimatesRequest,
  init?: RequestInit,
): Promise<CreatorCostEstimates> => {
  const { network, username } = req;

  const res = await creatorDetailsUsersFetch(
    `/${network}/${username}/cost-estimates`,
    {
      ...init,
      method: 'GET',
    },
  );

  const costEstimatesResults = await res.json<{
    data: CreatorCostEstimates;
  }>();

  return costEstimatesResults.data;
};

/**
 * Get Instagram media content and insights by username
 *
 * GET /api/v1/details/media
 */
export async function getCreatorMedia(
  req: CreatorMediaRequest,
  init?: RequestInit,
) {
  const params = makeParams(req);

  const res = await influencersGatewayV2Fetch(`/details/media?${params}`, init);

  const queryResults = await res.json<CreatorMediaResponse>();

  return queryResults;
}

/**
 * Search Creator Details Api for Similar influencers to the current one.
 *
 * GET /api/v1/users/:network/:username/similar-creators
 */
export const getSimilarCreators = async (
  req: SimilarCreatorsRequest,
  init?: RequestInit,
): Promise<SimilarCreatorsResponse> => {
  const { network, username } = req;

  const res = await creatorDetailsUsersFetch(
    `/${network}/${username}/similar-creators`,
    {
      ...init,
    },
  );

  const searchResults = await res.json<{
    data: SimilarCreatorsResponse;
  }>();

  return searchResults.data;
};

/**
 * Fetch the affable media for a creator's by category
 *
 * POST /api/v1/users/:network/:username/media
 */
export const getCreatorAffableMediaByCategory = async (
  req: CreatorAffableMediaRequest,
  init?: RequestInit,
): Promise<CreatorAffableMediaResponse> => {
  const { brandSafetyCategory, network, pageNumber, pageSize, username } = req;
  const params = makeParams({ pageNumber, pageSize });

  const res = await creatorDetailsUsersFetch(
    `/${network}/${username}/media?${params}`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify({ filters: { brandSafetyCategory } }),
    },
  );

  const results = await res.json<{
    data: CreatorAffableMediaResponse;
  }>();

  return results.data;
};

/**
 * Request audience stats estimates for a creator
 *
 * POST /api/v1/insights/:network/:username/audience-estimates
 */
export const createAudienceEstimateRequest = async (
  req: CreateAudienceEstimateRequest,
  init?: RequestInit,
): Promise<CreateAudienceEstimateResponse> => {
  const { network, username } = req;

  const res = await creatorDetailsInsightsFetch(
    `/${network}/${username}/audience-estimates`,
    {
      ...init,
      method: 'POST',
    },
  );

  const results = await res.json<{
    data: CreateAudienceEstimateResponse;
  }>();

  return results.data;
};

/**
 * Get Creator Content Distribution Insights by username (Ratio of Branded vs. Non-branded content by this creator)
 *
 * GET /api/v1/insights/:network/:username/content-distributions?period={$period}
 */
export const getCreatorContentDistributionInsights = async (
  req: ContentDistributionInsightRequest,
  init?: RequestInit,
): Promise<ContentDistributionInsightResponse> => {
  const { network, period, username } = req;
  const params = makeParams({ period });

  const res = await creatorDetailsInsightsFetch(
    `/${network}/${username}/content-distributions?${params}`,
    {
      ...init,
    },
  );

  const results = await res.json<{
    data: ContentDistributionInsightResponse;
  }>();

  return results.data;
};

/**
 * Request creator brand affinity
 * (brands the creator has mentioned or tagged in their posts)
 *
 * GET /api/v1/insights/:network/:username/brands
 */
export const getBrandAffinity = async (
  req: BrandAffinityRequest,
  init?: RequestInit,
): Promise<BrandAffinityResponse> => {
  const { isSponsored, network, sortBy, username } = req;
  const params = makeParams({ isSponsored, sortBy });

  const res = await creatorDetailsInsightsFetch(
    `/${network}/${username}/brands?${params}`,
    {
      ...init,
      method: 'GET',
    },
  );

  const results = await res.json<{
    data: BrandAffinityResponse;
  }>();

  return results.data;
};

/**
 * Request creator engagement timeline
 *
 * GET /api/v1/insights/:network/:username/engagement-timelines
 */
export const getCreatorEngagementTimeline = async (
  req: EngagementTimelineRequest,
  init?: RequestInit,
): Promise<EngagementTimelineResponse> => {
  const { network, username } = req;

  const res = await creatorDetailsInsightsFetch(
    `/${network}/${username}/engagement-timelines`,
    {
      ...init,
      method: 'GET',
    },
  );

  const results = await res.json<{
    data: EngagementTimelineResponse;
  }>((res) => ({
    data: {
      ...res.data,
      engagementTimeline: res.data.engagementTimeline.map((timepoint) => ({
        ...timepoint,
        date: timepoint.date ? new Date(timepoint.date) : undefined,
      })),
    },
  }));

  return results.data;
};

/**
 * Get Creator Follower Timeline Insights by username (Creator's follower timeline)
 *
 * GET /api/v1/insights/:network/:username/follower-timelines
 */
export const getCreatorFollowerTimelineInsights = async (
  req: FollowerTimelineRequest,
  init?: RequestInit,
): Promise<FollowerTimelineResponse> => {
  const { network, username } = req;

  const res = await creatorDetailsInsightsFetch(
    `/${network}/${username}/follower-timelines`,
    {
      ...init,
    },
  );

  const results = await res.json<{
    data: FollowerTimelineResponse;
  }>((res) => {
    const response = res.data;
    return {
      data: {
        ...response,
        data: response.data.map((timepoint) => ({
          ...timepoint,
          date: timepoint.date ? new Date(timepoint.date) : undefined,
        })),
      },
    };
  });

  return results.data;
};

/**
 * Get Creator Stories Engagement
 *
 * GET /api/v1/insights/:network/:username/stories
 */
export const getCreatorAverageStoryEngagement = async (
  req: AverageStoryEngagementRequest,
  init?: RequestInit,
): Promise<AverageStoryEngagement> => {
  const { network, username } = req;

  const res = await creatorDetailsInsightsFetch(
    `/${network}/${username}/stories`,
    {
      ...init,
    },
  );

  const results = await res.json<{
    data: AverageStoryEngagement;
  }>();

  return results.data;
};

export const downloadCreatorDetails = async (
  req: CreatorDetailsExportRequest,
): Promise<Blob> => {
  const res = await creatorDetailsUsersFetch(
    `/${req.network}/${req.username}/export.zip`,
  );

  return res.blob();
};

/**
 * Request creator brand affinity media
 * (media content where the creator has mentioned or tagged specific brands)
 *
 * POST /api/v1/insights/:network/:username/brands/affinity-media
 */
export const getBrandAffinityMedia = async (
  req: BrandAffinityMediaRequest,
  init?: RequestInit,
): Promise<BrandAffinityMediaResponse> => {
  const { brandUsernames, network, username } = req;
  const params = makeParams({ network });
  const res = await creatorDetailsInsightsFetch(
    `/${network}/${username}/brands/affinity-media?${params}`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify({ brandUsernames }),
    },
  );

  const results = await res.json<{
    data: BrandAffinityMediaResponse;
  }>();

  return results.data;
};

/**
 * Search for creators and retrieve their metadata
 *
 * POST /api/v1/users/search
 */
export const getCreatorMetadata = async (
  req: CreatorSearchRequest,
  init?: RequestInit,
): Promise<SearchCreatorUsersResponse> => {
  const { fields, filters, offset, size } = req;

  const affableRes = await creatorDetailsUsersFetch(
    `/search?${makeParams({ size: size, offset: offset })}`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify({
        fields,
        filters,
      }),
    },
  );

  const res = await affableRes.json<{
    data: SearchCreatorUsersResponse;
  }>();

  return res.data;
};

/**
 * Get Creator Media Safety Stats by username
 *
 * GET /api/v1/users/:network/:username/media-safety-stats
 */
export const getCreatorMediaSafetyStats = async (
  req: CreatorMediaSafetyStatsRequest,
  init?: RequestInit,
): Promise<CreatorMediaSafetyStatsResponse> => {
  const { network, username } = req;

  const res = await creatorDetailsUsersFetch(
    `/${network}/${username}/media-safety-stats`,
    {
      ...init,
      method: 'GET',
    },
  );
  const safetyStatsResult = await res.json<{
    data: CreatorMediaSafetyStatsResponse;
  }>(deserializeCreatorMediaSafetyStatsResponse);
  return safetyStatsResult.data;
};

/**
 * Update influencer metadata at organization level
 *
 * PUT /api/v1/users/:network/:username/metadata
 */
export const updateInfluencerMetadata = async (
  req: UpdateInfluencerMetadataApiRequest,
  init?: RequestInit,
): Promise<InfluencerMetadata> => {
  const { metadata, network, username } = req;

  const res = await creatorDetailsUsersFetch(
    `/${network}/${username}/metadata`,
    {
      ...init,
      method: 'PUT',
      body: JSON.stringify(metadata),
    },
  );

  const results = await res.json<UpdateInfluencerMetadataResponse>();

  return results.data;
};
